import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import { cursoPago, useAcessos } from '../state/acessos.ts';
import { SeloPago } from '../components/SeloPago.tsx';
import type { Track } from '../evaluator/types.ts';
import {
  loadCatalogo,
  type Catalogo as Conteudo,
  type ManifestEntry,
  type SubseccaoDoManifesto,
  type TopicoDoManifesto,
} from '../content/load.ts';
import {
  ordemDoCurso,
  progressoDaSeccao,
  progressoDaSubseccao,
  progressoDoCurso,
  progressoDoTopico,
  proximoTopico,
  seccaoActual,
  seccoesDoCurso,
  situacaoDoTopico,
  tituloDoTopico,
  type Progresso,
} from '../state/curso.ts';
import { useLessonProgress } from '../state/lessonProgress.ts';
import { useDesbloqueioTotal } from '../state/desbloqueio.ts';
import { useCursoActual } from '../state/cursoActual.ts';
import { Button, Callout, ProgressBar } from '../components/primitives/index.tsx';
import { EstadoACarregar } from '../components/brand/LoadingMark.tsx';
import { SiglaDaLinguagem } from './SiglaDaLinguagem.tsx';
import { t } from '../i18n/index.ts';
import './curso.css';

/**
 * ADR 0027 — a página do curso, à imagem da referência: as secções à
 * esquerda (os módulos), a secção seleccionada à direita com as suas
 * subsecções, e cada subsecção abre a lista de tópicos (as lições). O
 * progresso vê-se em cada nível — «1/6» secções, «2/3» tópicos —, por decisão
 * do dono do produto a 05/09/2026.
 *
 * Desenha-se **só com o manifesto**, como o catálogo: o desbloqueio de um
 * tópico segue a ordem da trilha, que é a mesma de que o compilador deriva os
 * pré-requisitos (`state/curso.ts`). É o que faz esta página abrir depressa
 * numa ligação má (RNF-002), que é o caso que interessa.
 *
 * A secção seleccionada vive na query (`?seccao=`) para poder ser ligada e
 * partilhada; sem ela, é a primeira por concluir.
 */
export function CursoRoute() {
  const { trackId } = useParams({ from: '/curso/$trackId' });
  // H-304 — o selo «Pago» no cabeçalho, a partir da cópia guardada dos acessos.
  const pago = useAcessos((s) => cursoPago(s, trackId));
  const { seccao: seccaoPedida } = useSearch({ from: '/curso/$trackId' });
  const [catalogo, setCatalogo] = useState<Conteudo | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [tentativa, setTentativa] = useState(0);
  // Em telemóvel a lista de secções é uma gaveta; em ecrã grande está sempre
  // à vista e este estado não faz nada (é a folha de estilos que decide).
  const [gavetaAberta, setGavetaAberta] = useState(false);
  const [subseccaoAberta, setSubseccaoAberta] = useState<string | null>(null);

  const progresso = useLessonProgress((s) => s.lessons);
  const concluidas = useMemo(
    () => new Set(Object.entries(progresso).filter(([, e]) => e.completedAt).map(([id]) => id)),
    [progresso],
  );
  const desbloqueado = useDesbloqueioTotal();
  const definirCursoActual = useCursoActual((s) => s.definir);
  const limparCursoActual = useCursoActual((s) => s.limpar);

  useEffect(() => {
    const controlador = new AbortController();
    setErro(null);
    setCatalogo(null);
    loadCatalogo(controlador.signal)
      .then(setCatalogo)
      .catch((err: unknown) => {
        if (controlador.signal.aborted) return;
        setErro(err instanceof Error ? err.message : String(err));
      });
    return () => controlador.abort();
    // Só `tentativa`: o catálogo é o mesmo para todos os cursos, e mudar de
    // curso sem desmontar (voltar no navegador, «Aprender» na barra) não tem
    // de o apagar e pedir outra vez com um ecrã de carregamento pelo meio.
  }, [tentativa]);

  const trilha = catalogo?.trilhas.find((x) => x.id === trackId);

  // O «Aprender» da barra passa a apontar para aqui. Só para um curso que
  // existe: um endereço errado não pode ficar guardado como o curso actual —
  // e um que deixou de existir (trilha renomeada ou retirada) limpa-se, para
  // «Aprender» voltar a levar ao catálogo em vez de a este ecrã.
  useEffect(() => {
    if (!catalogo) return;
    if (trilha) definirCursoActual(trilha.id);
    else limparCursoActual();
  }, [catalogo, trilha, definirCursoActual, limparCursoActual]);

  // Mudar de secção fecha a gaveta e a lista de tópicos, se estiverem abertas.
  useEffect(() => {
    setGavetaAberta(false);
    setSubseccaoAberta(null);
  }, [seccaoPedida]);

  if (erro) {
    return (
      <div className="curso">
        <Callout tone="error" role="alert">
          <p>{t('trilhas.erro.titulo')}</p>
          <p>{t('erro.semLigacao')}</p>
        </Callout>
        <Button onClick={() => setTentativa((n) => n + 1)}>{t('trilhas.erro.accao')}</Button>
      </div>
    );
  }

  if (!catalogo) {
    return (
      <div className="curso">
        <EstadoACarregar texto={t('trilhas.acarregar')} />
      </div>
    );
  }

  if (!trilha) {
    return (
      <div className="curso">
        <Callout tone="error" role="alert">
          <p>{t('curso.inexistente')}</p>
        </Callout>
        <Link to="/" className="btn btn--primary">
          {t('catalogo.voltar')}
        </Link>
      </div>
    );
  }

  const seccoes = seccoesDoCurso(catalogo.modulos, trilha.id);
  if (seccoes.length === 0) {
    return (
      <div className="curso">
        <Callout tone="info">
          <p>{t('curso.vazio')}</p>
        </Callout>
        <Link to="/" className="btn btn--secondary">
          {t('catalogo.voltar')}
        </Link>
      </div>
    );
  }

  const seleccionada =
    seccoes.find((m) => m.id === seccaoPedida) ?? seccaoActual(seccoes, concluidas, progresso)!;
  const posicao = seccoes.findIndex((m) => m.id === seleccionada.id);
  const seguinte = seccoes[posicao + 1];
  const doCurso = progressoDoCurso(seccoes, concluidas, progresso);
  const ordem = ordemDoCurso(seccoes);
  const proximo = proximoTopico(seccoes, concluidas, progresso);
  const subseccao = seleccionada.subseccoes.find((s) => s.id === subseccaoAberta) ?? null;

  return (
    <div className="curso entra-suave">
      {/* Uma ligação de regresso não é uma região de navegação: um `nav`
          aqui era um terceiro marco «navegação», sem nome, ao lado da barra e
          da lista de secções. */}
      <div className="curso__voltar">
        <Link to="/" className="voltar">
          <span aria-hidden="true">←</span>
          {t('curso.voltar')}
        </Link>
      </div>

      <div className={gavetaAberta ? 'curso__colunas curso__colunas--abertas' : 'curso__colunas'}>
        <aside className="curso__lado">
          <p className="cartao__etiqueta">{t('catalogo.trilhaEtiqueta')}</p>
          <p className="curso__lado-nome">{trilha.title}</p>
          {/* A lista de secções fica atrás de um botão em todos os ecrãs, e
              nasce oculta (decisão do dono do produto, 06/09/2026): quem entra
              quer a secção onde ia, não a lista toda. Em ecrã grande, aberta,
              a lista assenta ao lado do conteúdo; fechada, o conteúdo ocupa a
              largura toda. */}
          <button
            type="button"
            className="btn btn--secondary curso__alternar"
            aria-expanded={gavetaAberta}
            aria-controls="curso-seccoes"
            onClick={() => setGavetaAberta((aberta) => !aberta)}
          >
            {t('curso.seccoes.alternar', { feitas: doCurso.feitas, total: doCurso.total })}
            <span className="curso__alternar-seta" aria-hidden="true" />
          </button>
          <nav
            id="curso-seccoes"
            className={gavetaAberta ? 'curso__seccoes curso__seccoes--aberta' : 'curso__seccoes'}
            aria-label={t('curso.seccoes.aria', { curso: trilha.title })}
          >
            <p className="curso__seccoes-titulo" aria-hidden="true">
              {t('curso.seccoes')}
            </p>
            <ol className="seccoes">
              {seccoes.map((modulo, i) => (
                <LinhaDeSeccao
                  key={modulo.id}
                  modulo={modulo}
                  numero={i + 1}
                  trilha={trilha}
                  seleccionada={modulo.id === seleccionada.id}
                  concluidas={concluidas}
                  progresso={progresso}
                  aoEscolher={() => setGavetaAberta(false)}
                />
              ))}
            </ol>
          </nav>
        </aside>

        <main className="curso__principal">
          <header className="curso__cabecalho">
            <p className="cartao__etiqueta">
              {t('curso.etiqueta')}
              {pago ? <SeloPago /> : null}
            </p>
            <h1 className="curso__titulo">
              <SiglaDaLinguagem trilha={trilha.id} />
              {trilha.title}
            </h1>
            {proximo ? (
              <Link
                to="/licao/$lessonId"
                params={{ lessonId: proximo }}
                className="btn btn--primary curso__continuar"
              >
                {/* «Começar» só quando não há nada feito **neste** curso — o
                    conjunto de concluídas junta todas as trilhas, e quem
                    acabou JavaScript e abre Python pela primeira vez começa. */}
                {t(doCurso.estado === 'por-comecar' ? 'curso.comecar' : 'curso.continuar', {
                  topico: tituloDoTopico(seccoes, proximo),
                })}
              </Link>
            ) : (
              <p className="curso__concluido">
                <span aria-hidden="true">✓ </span>
                {t('curso.concluido')}
              </p>
            )}
          </header>

          <section className="curso__seccao" aria-labelledby="curso-seccao-titulo">
            <h2 className="curso__seccao-titulo" id="curso-seccao-titulo">
              {t('curso.seccao.numero', { n: posicao + 1, titulo: seleccionada.title })}
            </h2>
            <p className="curso__seccao-descricao">{seleccionada.description}</p>
            <ol
              className="subseccoes"
              aria-label={t('curso.subseccoes.aria', { seccao: seleccionada.title })}
            >
              {seleccionada.subseccoes.map((s, i) => (
                <LinhaDeSubseccao
                  key={s.id}
                  subseccao={s}
                  numero={i + 1}
                  concluidas={concluidas}
                  progresso={progresso}
                  abrir={() => setSubseccaoAberta(s.id)}
                />
              ))}
            </ol>
          </section>

          <SeccaoSeguinte trilha={trilha} seguinte={seguinte} />

          <Link
            to="/certificados"
            className="certificado"
            aria-label={t('curso.certificado.aria', {
              curso: trilha.title,
              percentagem: doCurso.percentagem,
              feitas: doCurso.feitas,
              total: doCurso.total,
            })}
          >
            <Medalha />
            <span className="certificado__corpo">
              <span className="certificado__titulo">{t('curso.certificado.titulo')}</span>
              <span className="certificado__barra" aria-hidden="true">
                <ProgressBar value={doCurso.percentagem} max={100} />
              </span>
              <span className="certificado__linha" aria-hidden="true">
                <span>{t('catalogo.percentagem', { n: doCurso.percentagem })}</span>
                <span className="contagem">
                  {t('catalogo.contagem', { feitas: doCurso.feitas, total: doCurso.total })}
                </span>
              </span>
            </span>
          </Link>
        </main>
      </div>

      <ListaDeTopicos
        subseccao={subseccao}
        seccoes={seccoes}
        ordem={ordem}
        concluidas={concluidas}
        progresso={progresso}
        desbloqueado={desbloqueado}
        fechar={() => setSubseccaoAberta(null)}
      />
    </div>
  );
}

/**
 * Uma secção na lista lateral: número, título, e ✓ ou «feitas/total» de
 * subsecções. A contagem é texto visível **e** vai no rótulo por extenso:
 * «0/7» à vista diz o tamanho; «0 de 7 subsecções concluídas» em voz alta diz
 * o mesmo a quem não vê a lista (RNF-023).
 */
function LinhaDeSeccao({
  modulo,
  numero,
  trilha,
  seleccionada,
  concluidas,
  progresso,
  aoEscolher,
}: {
  modulo: ManifestEntry;
  numero: number;
  trilha: Track;
  seleccionada: boolean;
  concluidas: ReadonlySet<string>;
  progresso: Progresso;
  /** Fecha a gaveta em telemóvel — também ao tocar na secção já escolhida. */
  aoEscolher: () => void;
}) {
  const conta = progressoDaSeccao(modulo, concluidas, progresso);
  // «lista-seccao» e não «seccao»: o catálogo já tem `.seccao` e
  // `.seccao__titulo` para os seus blocos, e as duas folhas viajam juntas.
  const classes = [
    'lista-seccao',
    `lista-seccao--${conta.estado}`,
    seleccionada ? 'lista-seccao--seleccionada' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <li className={classes}>
      <Link
        to="/curso/$trackId"
        params={{ trackId: trilha.id }}
        search={{ seccao: modulo.id }}
        replace
        onClick={aoEscolher}
        className="lista-seccao__ligacao"
        // «page» e não «true»: o router escreve `aria-current="page"` por
        // cima de tudo quando a query coincide, e a secção seleccionada por
        // omissão (sem query) tem de soar igual a quem ouve.
        aria-current={seleccionada ? 'page' : undefined}
        aria-label={t('curso.seccao.aria', {
          n: numero,
          titulo: modulo.title,
          feitas: conta.feitas,
          total: conta.total,
        })}
      >
        <span className="lista-seccao__numero" aria-hidden="true">
          {String(numero).padStart(2, '0')}.
        </span>
        <span className="lista-seccao__titulo" aria-hidden="true">
          {modulo.title}
        </span>
        <Estado conta={conta} />
      </Link>
    </li>
  );
}

/** ✓ quando concluída; senão «feitas/total». Decorativo: o rótulo já o diz. */
function Estado({ conta }: { conta: { feitas: number; total: number; estado: string } }) {
  return conta.estado === 'concluida' ? (
    <span className="selo" aria-hidden="true">
      ✓
    </span>
  ) : (
    <span className="contagem" aria-hidden="true">
      {t('catalogo.contagem', { feitas: conta.feitas, total: conta.total })}
    </span>
  );
}

/**
 * Uma subsecção: número, título, a etiqueta «Aprender»/«Prática», e ✓ ou
 * «feitas/total» de tópicos. É um botão e não uma ligação, porque abre a
 * lista de tópicos aqui mesmo, sem sair da página.
 */
function LinhaDeSubseccao({
  subseccao,
  numero,
  concluidas,
  progresso,
  abrir,
}: {
  subseccao: SubseccaoDoManifesto;
  numero: number;
  concluidas: ReadonlySet<string>;
  progresso: Progresso;
  abrir: () => void;
}) {
  const conta = progressoDaSubseccao(subseccao, concluidas, progresso);
  const tipo = t(`curso.subseccao.${subseccao.kind}`);

  return (
    <li className={`subseccao subseccao--${conta.estado} subseccao--${subseccao.kind}`}>
      <button
        type="button"
        className="subseccao__botao"
        onClick={abrir}
        aria-haspopup="dialog"
        aria-label={t('curso.subseccao.aria', {
          titulo: subseccao.title,
          tipo,
          feitas: conta.feitas,
          total: conta.total,
        })}
      >
        <span className="subseccao__numero" aria-hidden="true">
          {String(numero).padStart(2, '0')}
        </span>
        <span className="subseccao__titulo" aria-hidden="true">
          {subseccao.title}
        </span>
        <span className={`subseccao__tipo subseccao__tipo--${subseccao.kind}`} aria-hidden="true">
          {tipo}
        </span>
        <Estado conta={conta} />
      </button>
    </li>
  );
}

/**
 * A lista de tópicos de uma subsecção, numa caixa modal — o «pick a chapter»
 * da referência. É um `<dialog>` nativo: o foco fica preso, o Escape fecha, e
 * o leitor de ecrã sabe que está numa caixa sem que se escreva nada disso.
 *
 * Cada tópico é a lição: disponível abre, concluído abre para rever,
 * bloqueado diz o que falta (RF-014) e não é ligação nenhuma — um cadeado
 * sozinho não é accionável.
 */
function ListaDeTopicos({
  subseccao,
  seccoes,
  ordem,
  concluidas,
  progresso,
  desbloqueado,
  fechar,
}: {
  subseccao: SubseccaoDoManifesto | null;
  seccoes: ManifestEntry[];
  ordem: readonly string[];
  concluidas: ReadonlySet<string>;
  progresso: Progresso;
  desbloqueado: boolean;
  fechar: () => void;
}) {
  const caixa = useRef<HTMLDialogElement>(null);
  // Onde o gesto começou. O `click` dispara no antepassado comum de
  // `pointerdown` e `pointerup`: seleccionar texto na folha e largar no fundo
  // escurecido dava um clique no próprio `<dialog>` e fechava-o.
  const gestoNoFundo = useRef(false);

  useEffect(() => {
    const el = caixa.current;
    if (!el) return;
    if (subseccao && !el.open) el.showModal();
    if (!subseccao && el.open) el.close();
  }, [subseccao]);

  return (
    <dialog
      ref={caixa}
      className="topicos"
      aria-labelledby="topicos-titulo"
      onClose={fechar}
      // Tocar fora da folha fecha-a: o próprio `<dialog>` é o alvo quando o
      // gesto começa **e** acaba no fundo escurecido, e nunca quando cai no
      // conteúdo.
      onPointerDown={(e) => {
        gestoNoFundo.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && gestoNoFundo.current) fechar();
      }}
    >
      {subseccao ? (
        <div className="topicos__folha">
          <header className="topicos__cabecalho">
            <p className={`subseccao__tipo subseccao__tipo--${subseccao.kind}`}>
              {t(`curso.subseccao.${subseccao.kind}`)}
            </p>
            <h2 className="topicos__titulo" id="topicos-titulo">
              {subseccao.title}
            </h2>
          </header>
          <ol className="topicos__lista" aria-label={t('curso.topicos.aria', { subseccao: subseccao.title })}>
            {subseccao.topicos.map((topico, i) => (
              <Topico
                key={topico.id}
                topico={topico}
                numero={i + 1}
                seccoes={seccoes}
                ordem={ordem}
                concluidas={concluidas}
                progresso={progresso}
                desbloqueado={desbloqueado}
              />
            ))}
          </ol>
          <Button variant="secondary" block onClick={fechar}>
            {t('curso.topicos.fechar')}
          </Button>
        </div>
      ) : null}
    </dialog>
  );
}

function Topico({
  topico,
  numero,
  seccoes,
  ordem,
  concluidas,
  progresso,
  desbloqueado,
}: {
  topico: TopicoDoManifesto;
  numero: number;
  seccoes: ManifestEntry[];
  ordem: readonly string[];
  concluidas: ReadonlySet<string>;
  progresso: Progresso;
  desbloqueado: boolean;
}) {
  const situacao = situacaoDoTopico(topico.id, ordem, concluidas, desbloqueado);
  const conta = progressoDoTopico(topico, concluidas, progresso);
  const emCurso = situacao.estado === 'disponivel' && conta.estado === 'em-curso';
  const falta = situacao.estado === 'bloqueada' ? tituloDoTopico(seccoes, situacao.falta) : '';

  const rotulo =
    situacao.estado === 'bloqueada'
      ? t('curso.topico.aria.bloqueada', { titulo: topico.title, falta })
      : situacao.estado === 'concluida'
        ? t('curso.topico.aria.concluida', { titulo: topico.title, exercicios: topico.exercicios })
        : emCurso
          ? t('curso.topico.aria.emCurso', { titulo: topico.title, feitas: conta.feitas, total: conta.total })
          : t('curso.topico.aria.disponivel', { titulo: topico.title, exercicios: topico.exercicios });

  const meta =
    situacao.estado === 'bloqueada'
      ? t('curso.topico.bloqueado', { titulo: falta })
      : situacao.estado === 'concluida'
        ? t('curso.topico.concluido')
        : emCurso
          ? t('curso.topico.emCurso', { feitas: conta.feitas, total: conta.total })
          : t('curso.topico.exercicios', { n: topico.exercicios });

  // Numa ligação, o rótulo diz tudo e o interior esconde-se de quem ouve
  // (senão ouvia-se duas vezes). Num tópico bloqueado não há ligação nem
  // rótulo — um `span` sem papel não pode ter `aria-label` — e é o interior
  // que se lê, tal como está.
  const conteudo = (oculto: boolean) => (
    <>
      <span className="topico__marca" aria-hidden="true">
        {situacao.estado === 'concluida' ? '✓' : String(numero)}
      </span>
      <span className="topico__texto" aria-hidden={oculto ? 'true' : undefined}>
        <span className="topico__linha">
          <span className="topico__titulo">{topico.title}</span>
          {/* A consolidação é anunciada, e a matéria não: uma etiqueta em
              todas as linhas deixaria de assinalar seja o que for. */}
          {topico.kind === 'consolidacao' ? (
            <span className="topico__tipo">{t('trilho.consolidacao')}</span>
          ) : null}
        </span>
        <span className="topico__meta">{meta}</span>
        {emCurso ? (
          <span className="topico__barra">
            <span className="topico__barra-cheio" style={{ width: `${(conta.feitas / conta.total) * 100}%` }} />
          </span>
        ) : null}
      </span>
    </>
  );

  const classes = ['topico', `topico--${situacao.estado}`, emCurso ? 'topico--em-curso' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <li className={classes}>
      {situacao.estado === 'bloqueada' ? (
        // Não é ligação e não finge sê-lo: o que se lê é o número, o título
        // e «Depois de ‹título›», com o estado dito antes para quem ouve.
        <span className="topico__ligacao">
          <span className="sr-only">{t('curso.topico.bloqueadoEstado')} </span>
          {conteudo(false)}
        </span>
      ) : (
        <Link
          to="/licao/$lessonId"
          params={{ lessonId: topico.id }}
          className="topico__ligacao"
          aria-label={rotulo}
        >
          {conteudo(true)}
        </Link>
      )}
    </li>
  );
}

/**
 * O que vem a seguir. A descrição não é decorativa: é a frase que diz o que o
 * estudante vai saber fazer, e é ela que distingue «continuar» de «continuar
 * para quê». No fim do curso diz-se que acabou — sem prometer nada que a
 * página do certificado não diga por si.
 */
function SeccaoSeguinte({ trilha, seguinte }: { trilha: Track; seguinte: ManifestEntry | undefined }) {
  if (!seguinte) {
    return (
      <aside className="proximo proximo--fim">
        <p className="proximo__etiqueta">{t('proximo.fimEtiqueta')}</p>
        <p className="proximo__descricao">{t('proximo.fimTexto', { trilha: trilha.title })}</p>
        <Link to="/" className="btn btn--secondary">
          {t('catalogo.voltar')}
        </Link>
      </aside>
    );
  }

  return (
    <aside className="proximo" aria-labelledby="proximo-seccao">
      <p className="proximo__etiqueta">{t('curso.seguinte.etiqueta')}</p>
      <h2 className="proximo__titulo" id="proximo-seccao">
        {seguinte.title}
      </h2>
      <p className="proximo__descricao">{seguinte.description}</p>
      <Link
        to="/curso/$trackId"
        params={{ trackId: trilha.id }}
        search={{ seccao: seguinte.id }}
        className="btn btn--primary"
      >
        {t('curso.seguinte.accao')}
      </Link>
    </aside>
  );
}

/** A medalha do certificado, em formas: nada trazido de fora (RNF-001). */
function Medalha() {
  return (
    <svg className="certificado__medalha" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path className="medalha__fita" d="M16 26h16l4 18-12-6-12 6z" />
      <circle className="medalha__disco" cx="24" cy="18" r="14" />
      <circle className="medalha__miolo" cx="24" cy="18" r="8" />
    </svg>
  );
}
